/**
 * Reine Geometrie der Zeichenebene (Markierwerkzeug, V6 Z4): Rechtecke sind normiert 0 bis 1 (x, y = oben links; b = Breite, h = Höhe).
 * Keine Oberfläche, keine Rechte. Alle Funktionen halten das Ergebnis in der Seite (0 bis 1) und über der Mindestgröße.
 */
export interface MarkierRechteck {
  x: number;
  y: number;
  b: number;
  h: number;
}

export type MarkierGriff = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

/** Kleinste Kantenlänge eines Rechtecks (1 % der Seite). */
export const MIN_KANTE = 0.01;
/** Schrittweite der Pfeiltasten (0,5 % der Seite); mit Umschalt wirkt sie auf die Größe. */
export const TASTEN_SCHRITT = 0.005;

const rund = (n: number) => Math.round(n * 10000) / 10000;

/** Hält das Rechteck in der Seite und über der Mindestgröße (Position verschiebt sich, Größe wird nur gekürzt, wenn sie nicht passt). */
export function haltInSeite(r: MarkierRechteck): MarkierRechteck {
  const b = Math.min(1, Math.max(MIN_KANTE, r.b));
  const h = Math.min(1, Math.max(MIN_KANTE, r.h));
  const x = Math.min(1 - b, Math.max(0, r.x));
  const y = Math.min(1 - h, Math.max(0, r.y));
  return { x: rund(x), y: rund(y), b: rund(b), h: rund(h) };
}

export function verschiebe(r: MarkierRechteck, dx: number, dy: number): MarkierRechteck {
  return haltInSeite({ ...r, x: r.x + dx, y: r.y + dy });
}

/** Zieht an einem Griff; die gegenüberliegende Kante bleibt stehen. */
export function veraendereGroesse(r: MarkierRechteck, griff: MarkierGriff, dx: number, dy: number): MarkierRechteck {
  let links = r.x;
  let oben = r.y;
  let rechts = r.x + r.b;
  let unten = r.y + r.h;
  if (griff.includes("w")) links = Math.min(Math.max(0, links + dx), rechts - MIN_KANTE);
  if (griff.includes("e")) rechts = Math.max(Math.min(1, rechts + dx), links + MIN_KANTE);
  if (griff.includes("n")) oben = Math.min(Math.max(0, oben + dy), unten - MIN_KANTE);
  if (griff.includes("s")) unten = Math.max(Math.min(1, unten + dy), oben + MIN_KANTE);
  return haltInSeite({ x: links, y: oben, b: rechts - links, h: unten - oben });
}

/** Rechteck aus zwei Eckpunkten (in beliebiger Richtung gezogen). */
export function rechteckAusPunkten(ax: number, ay: number, bx: number, by: number): MarkierRechteck {
  const x0 = Math.min(ax, bx);
  const y0 = Math.min(ay, by);
  return haltInSeite({ x: x0, y: y0, b: Math.abs(bx - ax), h: Math.abs(by - ay) });
}

/** Ein gezogenes Rechteck zählt erst ab dieser Kantenlänge als gewollt (sonst war es ein Klick). */
export const ZIEH_MINDESTKANTE = 0.02;
export const istGezogen = (r: MarkierRechteck): boolean => r.b >= ZIEH_MINDESTKANTE && r.h >= ZIEH_MINDESTKANTE;

/** Rechteck in der Mitte der Seite für den Tastaturweg „Datenfeld hinzufügen“ (K5: Anlegen ohne Maus). */
export function standardRechteck(): MarkierRechteck {
  return { x: 0.4, y: 0.45, b: 0.2, h: 0.06 };
}

export type TastenAktion =
  | { art: "aendern"; rechteck: MarkierRechteck }
  | { art: "loeschen" }
  | { art: "oeffnen" }
  | { art: "abwaehlen" };

/**
 * Tastaturbedienung eines gewählten Rechtecks: Pfeile verschieben, Umschalt+Pfeile ändern die Größe (rechts/unten verlängern bzw.
 * verkürzen), Entf/Rück löschen, Enter öffnet die Zeile in der Liste, Esc wählt ab. Alles andere: `null` (Taste bleibt unberührt).
 */
export function tastenAktion(taste: string, umschalt: boolean, r: MarkierRechteck): TastenAktion | null {
  const richtung: Record<string, [number, number]> = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
  };
  const d = richtung[taste];
  if (d) {
    const dx = d[0] * TASTEN_SCHRITT;
    const dy = d[1] * TASTEN_SCHRITT;
    return { art: "aendern", rechteck: umschalt ? veraendereGroesse(r, "se", dx, dy) : verschiebe(r, dx, dy) };
  }
  if (taste === "Delete" || taste === "Backspace") return { art: "loeschen" };
  if (taste === "Enter") return { art: "oeffnen" };
  if (taste === "Escape") return { art: "abwaehlen" };
  return null;
}

export const gleich = (a: MarkierRechteck, b: MarkierRechteck): boolean => a.x === b.x && a.y === b.y && a.b === b.b && a.h === b.h;
